/* ==========================================================================
   Kopstorm CRO Suite - Wave 6 behaviour
   W67 search overlay · W68 bundle buy box · W69 buy box copy arm

   Two independent modules in one file, both gated on the KopstormCRO flag
   classes so the control arm behaves exactly like the live theme.

   W69 needs no JavaScript: the saving line and the marginal price line are
   both rendered server side and CSS decides which one is visible.

   The buy box never intercepts the theme's add to cart. Concept's own
   ProductForm already reads every checked input[name="bundles"] that points
   at the product form and posts one multi item /cart/add.js, so the buy box
   only has to tick and untick those inputs. That keeps error handling, the
   cart drawer refresh, section bundling and the pubsub events exactly as the
   theme wrote them.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  var W6 = window.KopstormW6 || {};
  var copy = W6.copy || {};
  var conf = W6.settings || {};

  function t(key, fallback) {
    var value = copy[key];
    return typeof value === 'string' && value !== '' ? value : fallback;
  }

  function has(flag) {
    return root.classList.contains('kop-' + flag);
  }

  function on(flag) {
    return has(flag) || has('w6editor') || has('w6preview');
  }

  function money(cents) {
    var theme = window.theme;
    if (theme && theme.Currency && theme.Currency.formatMoney && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    /* Only reached if theme.js has not booted yet. The server rendered value
       is already correct at that point, so returning null tells the caller to
       leave the existing text alone rather than print an unformatted number. */
    return null;
  }

  function fill(token, values) {
    return String(token).replace(/\[([a-z0-9_]+)\]/gi, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
    });
  }

  /* ------------------------------------------------------------------
     Scroll lock

     Shared by both overlays and reference counted, because the picker can be
     opened while the search overlay is already open and the first one to close
     must not release the page underneath the other.
     ------------------------------------------------------------------ */
  var lockCount = 0;
  var lockedAt = 0;

  function lockScroll() {
    lockCount++;
    if (lockCount > 1) return;
    lockedAt = window.pageYOffset || doc.documentElement.scrollTop || 0;
    /* Pinning the body removes the scrollbar, and the page under the overlay
       jumps sideways by its width. Hold the gap open while locked. */
    var gutter = window.innerWidth - doc.documentElement.clientWidth;
    if (gutter > 0) doc.body.style.paddingRight = gutter + 'px';
    doc.body.style.top = (-lockedAt) + 'px';
    doc.body.classList.add('kop6-search-open');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;
    doc.body.classList.remove('kop6-search-open');
    doc.body.style.top = '';
    doc.body.style.paddingRight = '';
    /* instant, so closing an overlay never animates the page back */
    window.scrollTo({ top: lockedAt, behavior: 'instant' });
  }

  /* ======================================================================
     W67 · Search overlay
     ====================================================================== */

  var Search = (function () {
    var STORE_KEY = 'kopstorm:w6:recent';
    var MAX_RECENT = 6;

    var el = null;
    var input = null;
    var browse = null;
    var results = null;
    var target = null;
    var clearBtn = null;
    var viewAll = null;
    var stateEmpty = null;
    var skeleton = null;
    var nav = [];
    var navIndex = -1;
    /* The visible highlight belongs to the keyboard. A pointer still moves the
       index, so Enter picks whatever is under the cursor, but drawing the ring
       on hover as well made every card look like it had a hard border. */
    var navByKeyboard = false;
    var recentSec = null;
    var recentList = null;
    var lastFocus = null;
    var controller = null;
    var cache = new Map();
    var timer = null;

    function read() {
      try {
        var raw = window.localStorage.getItem(STORE_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter(function (v) { return typeof v === 'string' && v; }) : [];
      } catch (e) {
        return [];
      }
    }

    function write(list) {
      try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
      } catch (e) { /* private mode, recent searches are optional */ }
    }

    function remember(term) {
      if (!conf.recent) return;
      var value = String(term || '').trim();
      if (value.length < 2) return;
      var list = read().filter(function (v) { return v.toLowerCase() !== value.toLowerCase(); });
      list.unshift(value);
      write(list);
    }

    function renderRecent() {
      if (!recentSec || !recentList || !conf.recent) return;
      var list = read();
      recentList.textContent = '';
      if (!list.length) {
        recentSec.hidden = true;
        return;
      }
      list.forEach(function (term) {
        var li = doc.createElement('li');
        var chip = doc.createElement('button');
        chip.type = 'button';
        chip.className = 'kop6-chip';
        chip.setAttribute('data-kop6-term', term);
        chip.appendChild(doc.createTextNode(term));

        var remove = doc.createElement('span');
        remove.className = 'kop6-chip__remove';
        remove.setAttribute('data-kop6-forget', term);
        remove.setAttribute('role', 'button');
        remove.setAttribute('tabindex', '0');
        remove.setAttribute('aria-label', fill(t('forget', 'Remove [term]'), { term: term }));
        remove.innerHTML = '<svg class="kop6-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
        chip.appendChild(remove);

        li.appendChild(chip);
        recentList.appendChild(li);
      });
      recentSec.hidden = false;
    }

    /* ------------------------------------------------------------------
       Keyboard navigation

       The stock predictive search only handles Escape, so arrowing through
       results is new. Every activatable row in both states carries
       data-kop6-nav, and the highlight roves across whichever set is
       currently visible. Focus itself stays in the input the whole time and
       the active row is announced with aria-activedescendant, which is what
       a combobox is supposed to do: typing never has to be interrupted to
       pick a result.
       ------------------------------------------------------------------ */

    function collect() {
      var scope = (browse && !browse.hidden) ? browse : results;
      nav = scope ? Array.prototype.filter.call(
        scope.querySelectorAll('[data-kop6-nav]'),
        function (node) { return !node.hidden && node.offsetParent !== null; }
      ) : [];

      nav.forEach(function (node, i) {
        if (!node.id) node.id = 'KopW6Nav-' + i;
        node.setAttribute('role', 'option');
        node.setAttribute('aria-selected', 'false');
      });

      highlight(-1);
    }

    function highlight(index) {
      if (nav[navIndex]) {
        nav[navIndex].classList.remove('is-highlighted');
        nav[navIndex].setAttribute('aria-selected', 'false');
      }

      navIndex = index;

      if (index < 0 || !nav[index]) {
        input.removeAttribute('aria-activedescendant');
        return;
      }

      var node = nav[index];
      node.classList.toggle('is-highlighted', navByKeyboard);
      node.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', node.id);
      /* block: 'nearest' so arrowing does not yank the panel around when the
         next row is already on screen. */
      node.scrollIntoView({ block: 'nearest' });
    }

    function move(step) {
      if (!nav.length) collect();
      if (!nav.length) return;

      var next = navIndex + step;
      if (next < 0) next = nav.length - 1;
      if (next >= nav.length) next = 0;
      highlight(next);
    }

    /* ------------------------------------------------------------------
       Looping typewriter

       The theme's search-typed element types one opening line, then the
       placeholder, then stops. Its start() and reset() are both awaitable and
       drive the same Motion width animation and blinking cursor, so the loop
       here just calls them in turn rather than reimplementing the effect.
       The element hides itself the moment the shopper types, which is why the
       loop checks that on every pass instead of tracking it separately.
       ------------------------------------------------------------------ */

    var typedTimer = null;
    var typedRun = 0;

    function typedPhrases() {
      var raw = conf.typed || '';
      var list = String(raw).split(',').map(function (v) { return v.trim(); }).filter(Boolean);
      return list.length ? list : [t('typed_start', '')].filter(Boolean);
    }

    function typedStop() {
      typedRun++;
      window.clearTimeout(typedTimer);
    }

    /* The theme's element hides itself on pointerdown and on the input event,
       which covers a shopper typing but NOT the field being filled in code: a
       popular term, a recent search, a voice result or a seeded open all set
       .value directly and fire nothing. The animation then ran underneath the
       query and the two strings drew on top of each other. */
    function typedSync() {
      var host = el.querySelector('[data-kop6-typed]');
      if (!host) return;

      var empty = input.value.length === 0;

      if (!empty) {
        typedStop();
        host.setAttribute('hidden', '');
        return;
      }

      if (host.hasAttribute('hidden')) {
        host.removeAttribute('hidden');
        typedLoop();
      }
    }

    function typedLoop() {
      var host = el.querySelector('[data-kop6-typed]');
      var node = el.querySelector('search-typed');
      if (!host) return;

      /* If the element never upgraded, search.js is not on the page. The
         placeholder is transparent underneath the typewriter, so the bar would
         read as empty. Drop the layer and let the real placeholder show. */
      if (!node || typeof node.start !== 'function') {
        if (host.parentNode) host.parentNode.removeChild(host);
        return;
      }

      var phrases = typedPhrases();
      if (!phrases.length) return;

      /* Two things the theme's own init() would normally do. It is triggered by
         Motion.inView from the constructor, and this element is inside a hidden
         overlay at parse time, so it may not have run: the cursor would be
         missing and start() throws on it. Claiming initialized also stops
         inView firing the theme's own one-shot sequence into the middle of
         this loop. */
      try {
        if (typeof node.insertCursor === 'function' && !node.cursor) node.insertCursor();
      } catch (e) { /* cursor is decorative, carry on without it */ }
      node.initialized = true;

      typedStop();
      var run = typedRun;
      var i = 0;

      function alive() {
        return run === typedRun && !el.hidden && !host.hasAttribute('hidden');
      }

      function step() {
        if (!alive()) return;

        Promise.resolve(node.start(phrases[i], 0))
          .then(function () {
            if (!alive()) return;
            /* Hold the finished line long enough to read before wiping it. */
            return new Promise(function (done) { typedTimer = window.setTimeout(done, 1800); });
          })
          .then(function () {
            if (!alive()) return;
            return node.reset();
          })
          .then(function () {
            if (!alive()) return;
            i = (i + 1) % phrases.length;
            typedTimer = window.setTimeout(step, 220);
          })
          .catch(function () { /* Motion was interrupted, nothing to recover */ });
      }

      step();
    }

    function buildUrl(query) {
      var routes = (window.theme && window.theme.routes) || {};
      var base = (routes.shop_url || window.location.origin) + (routes.predictive_search_url || '/search/suggest');
      var url = new URL(base, window.location.href);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[limit]', String(conf.limit || 6));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', 'kopstorm-w6-results');
      return url.toString();
    }

    function searchUrl(query) {
      var routes = (window.theme && window.theme.routes) || {};
      var base = routes.search_url || '/search';
      return base + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';
    }

    /* Shopify styles the matched term inside query suggestions but not inside
       product titles, so the same emphasis is applied here. Written with the
       DOM rather than innerHTML, so a product title can never inject markup. */
    function mark(query) {
      var needle = String(query || '').trim().toLowerCase();
      if (needle.length < 2) return;

      target.querySelectorAll('[data-kop6-mark]').forEach(function (node) {
        var text = node.textContent;
        var at = text.toLowerCase().indexOf(needle);
        if (at === -1) return;

        var frag = doc.createDocumentFragment();
        frag.appendChild(doc.createTextNode(text.slice(0, at)));
        var hit = doc.createElement('mark');
        hit.textContent = text.slice(at, at + needle.length);
        frag.appendChild(hit);
        frag.appendChild(doc.createTextNode(text.slice(at + needle.length)));

        node.textContent = '';
        node.appendChild(frag);
      });
    }

    function loading(state) {
      if (skeleton) skeleton.hidden = !state;
      if (target) target.hidden = state;
      el.toggleAttribute('data-loading', state);
    }

    function paint(html, query) {
      if (!target) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var next = parsed.getElementById('KopW6Results');
      target.textContent = '';
      if (next) {
        while (next.firstChild) target.appendChild(next.firstChild);
      }
      var found = target.querySelector('[data-kop6-found]');
      var count = found ? found.getAttribute('data-kop6-found') : '0';
      if (stateEmpty) stateEmpty.hidden = count !== '0';
      mark(query);
      loading(false);
      collect();
    }

    function request(query) {
      var url = buildUrl(query);

      if (cache.has(url)) {
        paint(cache.get(url), query);
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();
      loading(true);

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Kopstorm search ' + response.status);
          return response.text();
        })
        .then(function (html) {
          cache.set(url, html);
          if (cache.size > 30) cache.delete(cache.keys().next().value);
          paint(html, query);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          loading(false);
          /* A failed suggest call must never trap the shopper in an empty
             overlay, so fall through to the full results page. */
          if (stateEmpty) stateEmpty.hidden = false;
        });
    }

    function update() {
      var query = input.value.trim();

      if (viewAll) {
        viewAll.hidden = query.length === 0;
        if (query) {
          viewAll.href = searchUrl(query);
          viewAll.textContent = fill(t('view_all', 'See all results for [query]'), { query: query });
        }
      }
      if (clearBtn) clearBtn.hidden = query.length === 0;

      input.setAttribute('aria-expanded', query.length >= 2 ? 'true' : 'false');
      typedSync();

      if (query.length < 2) {
        browse.hidden = false;
        results.hidden = true;
        if (target) target.textContent = '';
        if (stateEmpty) stateEmpty.hidden = true;
        loading(false);
        collect();
        return;
      }

      browse.hidden = true;
      results.hidden = false;
      request(query);
    }

    function open(seed) {
      if (!el) return;
      lastFocus = doc.activeElement;
      el.hidden = false;
      renderRecent();
      /* Two frames: one to get the element out of [hidden], one so the
         transition has a starting frame to animate from. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { el.classList.add('is-open'); });
      });
      lockScroll();
      if (typeof seed === 'string' && seed) input.value = seed;
      update();
      collect();
      setTimeout(function () { input.focus(); }, 60);
      /* After the panel has painted, so the width animation has a real box to
         measure. search-typed bails when scrollWidth is 0. */
      /* Only when the field is empty. A seeded open already has a query in it. */
      setTimeout(function () {
        if (input.value.length === 0) typedLoop();
      }, 260);
    }

    function close() {
      if (!el || el.hidden) return;
      typedStop();
      highlight(-1);
      el.classList.remove('is-open');
      unlockScroll();
      window.setTimeout(function () { el.hidden = true; }, 220);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function isTrigger(node) {
      if (!node || !node.closest) return null;
      return node.closest('[data-kop6-search-open], a[aria-controls="SearchDrawer"], .search-drawer-button, [aria-controls="SearchDrawer"][is="magnet-link"]');
    }

    function init() {
      el = doc.getElementById('KopW6Search');
      if (!el) return;

      input = el.querySelector('[data-kop6-input]');
      browse = el.querySelector('[data-kop6-browse]');
      results = el.querySelector('[data-kop6-results]');
      target = el.querySelector('#KopW6Results');
      clearBtn = el.querySelector('[data-kop6-clear]');
      viewAll = el.querySelector('[data-kop6-viewall]');
      stateEmpty = el.querySelector('[data-kop6-empty]');
      skeleton = el.querySelector('[data-kop6-skeleton]');
      recentSec = el.querySelector('[data-kop6-recent]');
      recentList = el.querySelector('[data-kop6-recent-list]');

      if (!input || !browse || !results) return;

      /* Take the search triggers over at capture phase. The theme binds its
         own drawer handler on the element, so a capture listener on document
         is the only place that reliably runs first. */
      doc.addEventListener('click', function (event) {
        if (!on('w67')) return;
        var trigger = isTrigger(event.target);
        if (!trigger) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        open();
      }, true);

      /* The T45 persistent header search bar is a real form. When the overlay
         is live it becomes an entry point rather than a second search. */
      doc.addEventListener('focusin', function (event) {
        if (!on('w67')) return;
        var field = event.target.closest && event.target.closest('.kop-header-search input[type="search"]');
        if (!field) return;
        field.blur();
        open(field.value);
      });

      doc.addEventListener('submit', function (event) {
        if (!on('w67')) return;
        var form = event.target;
        if (!form.classList || !form.classList.contains('kop-header-search')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        var field = form.querySelector('input[type="search"]');
        open(field ? field.value : '');
      }, true);

      el.addEventListener('click', function (event) {
        var node = event.target;

        if (node.closest('[data-kop6-close]')) {
          event.preventDefault();
          close();
          return;
        }

        var forget = node.closest('[data-kop6-forget]');
        if (forget) {
          event.preventDefault();
          event.stopPropagation();
          var gone = forget.getAttribute('data-kop6-forget');
          write(read().filter(function (v) { return v !== gone; }));
          renderRecent();
          return;
        }

        if (node.closest('[data-kop6-back]')) {
          event.preventDefault();
          input.value = '';
          update();
          collect();
          input.focus();
          return;
        }

        var term = node.closest('[data-kop6-term]');
        if (term) {
          event.preventDefault();
          input.value = term.getAttribute('data-kop6-term');
          update();
          input.focus();
          return;
        }

        var link = node.closest('a[href]');
        if (link) remember(input.value);
      });

      input.addEventListener('input', function () {
        typedStop();
        window.clearTimeout(timer);
        timer = window.setTimeout(update, 250);
      });

      input.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') { event.preventDefault(); navByKeyboard = true; move(1); return; }
        if (event.key === 'ArrowUp') { event.preventDefault(); navByKeyboard = true; move(-1); return; }

        if (event.key === 'Home' && nav.length) { event.preventDefault(); navByKeyboard = true; highlight(0); return; }
        if (event.key === 'End' && nav.length) { event.preventDefault(); navByKeyboard = true; highlight(nav.length - 1); return; }

        if (event.key === 'Escape' && navIndex > -1) {
          /* First Escape drops the highlight, a second one closes the panel.
             Closing on the first press loses the shopper's typed query for
             what reads to them as an undo. */
          event.preventDefault();
          event.stopPropagation();
          highlight(-1);
          return;
        }

        if (event.key !== 'Enter') return;

        event.preventDefault();

        /* A highlighted row wins over the form's own submit. */
        if (nav[navIndex]) {
          remember(input.value);
          nav[navIndex].click();
          return;
        }

        var query = input.value.trim();
        if (!query) return;
        remember(query);
        window.location.href = searchUrl(query);
      });

      /* Pointer and keyboard must not fight over the highlight. */
      el.addEventListener('pointermove', function (event) {
        var row = event.target.closest && event.target.closest('[data-kop6-nav]');
        if (!row) return;
        var at = nav.indexOf(row);
        if (at > -1 && at !== navIndex) {
          navByKeyboard = false;
          highlight(at);
        }
      });

      /* Mobile keyboards fire the form's submit rather than a keydown for the
         go key, so the query is remembered here too and the form is then left
         to submit natively. */
      var form = el.querySelector('[data-kop6-form]');
      if (form) {
        form.addEventListener('submit', function () { remember(input.value); });
      }

      if (clearBtn) {
        clearBtn.addEventListener('click', function (event) {
          event.preventDefault();
          input.value = '';
          update();
          collect();
          input.focus();
        });
      }

      doc.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        if (el.hidden) return;
        close();
      });

      /* Keep focus inside the panel while it is open. */
      el.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab') return;
        var focusable = el.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
        var list = Array.prototype.filter.call(focusable, function (node) {
          return node.offsetParent !== null || node === doc.activeElement;
        });
        if (!list.length) return;
        var first = list[0];
        var last = list[list.length - 1];
        if (event.shiftKey && doc.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && doc.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      doc.addEventListener('kopstorm:flag', function () {
        if (!on('w67') && !el.hidden) close();
      });

      window.KopstormSearch = { open: open, close: close };
    }

    return { init: init };
  })();

  /* ======================================================================
     W68 · Bundle buy box
     ====================================================================== */

  var BuyBox = (function () {
    /* buy-buttons.liquid also renders inside the quick view modal and in the
       featured product section, so a page can legitimately carry more than one
       buy box. Everything below is therefore scoped to a box element rather
       than to a single module level variable, and every listener is delegated
       from document so a box injected later by the modal fetch works with no
       re-initialisation. */

    var activeSlot = null;

    function scopeOf(node) {
      return node.getAttribute('data-kop6-scope');
    }

    function pickerFor(box) {
      var scope = scopeOf(box);
      return doc.querySelector('[data-kop6-picker][data-kop6-scope="' + (window.CSS && CSS.escape ? CSS.escape(scope) : scope) + '"]');
    }

    function boxFor(picker) {
      var scope = scopeOf(picker);
      return doc.querySelector('[data-kop6-buybox][data-kop6-scope="' + (window.CSS && CSS.escape ? CSS.escape(scope) : scope) + '"]');
    }

    function candidates(picker) {
      if (!picker) return [];
      return Array.prototype.slice.call(picker.querySelectorAll('[data-kop6-option]'));
    }

    function tiers(box) {
      return Array.prototype.slice.call(box.querySelectorAll('[data-kop6-tier]'));
    }

    function selectedTier(box) {
      return box.querySelector('[data-kop6-tier].is-selected');
    }

    /* The chosen kits sit in a panel below the tier list rather than inside the
       tier, which is what lets the three layouts share one DOM. Everything is
       therefore looked up by the tier number the two elements agree on. */
    function panelOf(box, tier) {
      var n = tier.getAttribute('data-tier');
      return box.querySelector('[data-kop6-panel][data-tier="' + n + '"]');
    }

    function slotsOf(box, tier) {
      var panel = panelOf(box, tier);
      return panel ? Array.prototype.slice.call(panel.querySelectorAll('[data-kop6-slot]')) : [];
    }

    function chosenHandles(box, tier) {
      return slotsOf(box, tier).map(function (slot) { return slot.getAttribute('data-handle'); }).filter(Boolean);
    }

    /* --- pricing ------------------------------------------------------- */

    function tierNumbers(box, tier) {
      var basePrice = parseInt(box.getAttribute('data-base-price'), 10) || 0;
      var baseCompare = parseInt(box.getAttribute('data-base-compare'), 10) || basePrice;
      var pct = parseFloat(tier.getAttribute('data-discount')) || 0;

      var sum = basePrice;
      var compare = baseCompare;
      var added = 0;

      slotsOf(box, tier).forEach(function (slot) {
        var price = parseInt(slot.getAttribute('data-price'), 10) || 0;
        var cmp = parseInt(slot.getAttribute('data-compare'), 10) || price;
        sum += price;
        compare += cmp;
        added += price;
      });

      var keep = 100 - pct;
      var total = Math.round(sum * keep / 100);
      /* A tier is measured against the sum of the kits' own CURRENT prices.
         Measuring against compare at blended the store's everyday markdown into
         the bundle discount, which both overstated the offer and printed a
         saving no Shopify discount would apply at checkout. compare is still
         read so a kit with no compare at falls back to its price. */
      var list = pct > 0 ? sum : Math.max(compare, sum);

      return {
        total: total,
        list: list,
        saving: Math.max(0, list - total),
        addedDiscounted: Math.round(added * keep / 100),
        slots: slotsOf(box, tier).length,
        count: slotsOf(box, tier).length + 1
      };
    }

    function repaintTier(box, tier) {
      var n = tierNumbers(box, tier);

      var amount = tier.querySelector('[data-kop6-amount]');
      var totalText = money(n.total);
      if (amount && totalText) amount.textContent = totalText;

      var compare = tier.querySelector('[data-kop6-compare]');
      if (compare) {
        var listText = n.saving > 0 ? money(n.list) : '';
        if (n.saving > 0) {
          if (listText) compare.textContent = listText;
        } else {
          compare.textContent = '';
        }
      }

      /* Sale badge states the TIER discount, not the saving against compare at.
         Measured against list it blended the everyday markdown into the bundle
         discount and read 18 and 20 percent for a 10 and 15 percent ladder,
         which both overstates the offer and flattens the step between the two
         tiers. This number is now the same one the Shopify automatic discount
         applies at checkout. */
      var pct = tier.querySelector('[data-kop6-pct]');
      if (pct) {
        var share = Math.round(parseFloat(tier.getAttribute('data-discount')) || 0);
        pct.textContent = share > 0 ? fill(t('save_pct', 'Save [pct]%'), { pct: share }) : '';
      }

      /* Card layout thumbnails follow whatever the shopper has chosen. */
      slotsOf(box, tier).forEach(function (slot) {
        var thumb = tier.querySelector('[data-kop6-thumb="' + slot.getAttribute('data-index') + '"]');
        var source = slot.querySelector('img');
        if (!thumb || !source) return;
        var img = thumb.querySelector('img');
        if (!img) {
          img = doc.createElement('img');
          img.loading = 'lazy';
          thumb.appendChild(img);
        }
        img.src = source.currentSrc || source.src;
        if (source.srcset) img.srcset = source.srcset;
        img.sizes = '54px';
        img.alt = '';
      });

      var panel = panelOf(box, tier);
      var saving = panel && panel.querySelector('[data-kop6-saving]');
      if (saving) {
        if (n.saving > 0) {
          var savingText = money(n.saving);
          saving.textContent = savingText ? fill(t('you_save', 'You save [amount]'), { amount: savingText }) : '';
        } else {
          saving.textContent = '';
        }
      }

      var marginal = panel && panel.querySelector('[data-kop6-marginal]');
      if (marginal) {
        var lastSlot = slotsOf(box, tier).slice(-1)[0];
        if (lastSlot && n.slots > 0) {
          var each = money(Math.round(n.addedDiscounted / n.slots));
          var name = lastSlot.getAttribute('data-name') || '';
          marginal.textContent = each ? fill(t('marginal', 'Adds [name] for [amount]'), { name: name, amount: each }) : '';
        } else {
          marginal.textContent = '';
        }
      }
    }

    function syncSummary(box) {
      var tier = selectedTier(box);
      if (!tier) return;
      var n = tierNumbers(box, tier);
      var value = box.querySelector('[data-kop6-summary-value]');
      var wrap = box.querySelector('[data-kop6-summary]');
      var text = money(n.total);
      if (value && text) value.textContent = text;
      if (wrap) wrap.hidden = n.count < 2;
    }

    function repaintAll(box) {
      tiers(box).forEach(function (tier) { repaintTier(box, tier); });
      syncSummary(box);
      paintButton(box);
    }

    /* --- cart wiring --------------------------------------------------- */

    function syncInputs(box) {
      var host = box.querySelector('[data-kop6-inputs]');
      if (!host) return;

      host.textContent = '';

      if (!on('w68')) return;

      var tier = selectedTier(box);
      if (!tier) return;

      slotsOf(box, tier).forEach(function (slot) {
        var variantId = slot.getAttribute('data-variant');
        if (!variantId) return;
        var field = doc.createElement('input');
        field.type = 'checkbox';
        field.name = 'bundles';
        field.value = variantId;
        field.setAttribute('data-min', '1');
        field.setAttribute('data-price', slot.getAttribute('data-price') || '0');
        field.checked = true;
        field.hidden = true;
        /* Deliberately NO form attribute on the input itself. The wrapper
           carries it, which is how the theme's own product_bundle block is
           built, so ProductForm's [form="id"] input[name="bundles"]:checked
           still finds it. Putting form on the input would also enrol it in
           the form's FormData, and prepareFormData copies every unrecognised
           field onto the cart payload, so /cart/add.js would receive a stray
           bundles key alongside the items array. */
        host.appendChild(field);
      });
    }

    /* --- slot rendering ------------------------------------------------ */

    function paintSlot(slot, option) {
      slot.setAttribute('data-handle', option.getAttribute('data-handle'));
      slot.setAttribute('data-variant', option.getAttribute('data-variant'));
      slot.setAttribute('data-price', option.getAttribute('data-price'));
      slot.setAttribute('data-compare', option.getAttribute('data-compare'));
      slot.setAttribute('data-name', option.getAttribute('data-name'));

      var name = slot.querySelector('[data-kop6-slotname]');
      if (name) name.textContent = option.getAttribute('data-name') || '';

      var media = slot.querySelector('[data-kop6-slotmedia]');
      var source = option.querySelector('img');
      if (media && source) {
        var img = media.querySelector('img');
        if (!img) {
          img = doc.createElement('img');
          img.loading = 'lazy';
          img.width = 92;
          img.height = 92;
          media.appendChild(img);
        }
        img.src = source.currentSrc || source.src;
        if (source.srcset) img.srcset = source.srcset;
        img.sizes = '46px';
        img.alt = '';
      }
    }

    /* Two slots may not hold the same kit, so a swap that collides pushes the
       other slot onto the next free candidate rather than silently allowing a
       duplicate line. */
    function resolveCollisions(box, tier, changed) {
      var picker = pickerFor(box);
      var used = {};
      slotsOf(box, tier).forEach(function (slot) {
        var handle = slot.getAttribute('data-handle');
        if (!handle) return;
        if (slot === changed || !used[handle]) {
          used[handle] = true;
          return;
        }
        var free = candidates(picker).filter(function (option) {
          return !used[option.getAttribute('data-handle')];
        })[0];
        if (free) {
          paintSlot(slot, free);
          used[free.getAttribute('data-handle')] = true;
        }
      });
    }

    function selectTier(box, tier) {
      tiers(box).forEach(function (node) {
        var isTarget = node === tier;
        node.classList.toggle('is-selected', isTarget);
        var button = node.querySelector('[data-kop6-tierbtn]');
        if (button) button.setAttribute('aria-checked', isTarget ? 'true' : 'false');

        var panel = panelOf(box, node);
        if (panel) panel.hidden = !isTarget;
      });
      syncInputs(box);
      syncSummary(box);
      paintButton(box);
    }

    /* --- picker sheet --------------------------------------------------- */

    function tierOfSlot(box, slot) {
      var panel = slot.closest('[data-kop6-panel]');
      if (!panel) return null;
      return box.querySelector('[data-kop6-tier][data-tier="' + panel.getAttribute('data-tier') + '"]');
    }

    function openPicker(slot) {
      var box = slot.closest('[data-kop6-buybox]');
      var picker = pickerFor(box);
      if (!picker) return;

      activeSlot = slot;

      var tier = tierOfSlot(box, slot);
      if (!tier) return;
      var taken = chosenHandles(box, tier);
      var current = slot.getAttribute('data-handle');

      candidates(picker).forEach(function (option) {
        var handle = option.getAttribute('data-handle');
        option.classList.toggle('is-active', handle === current);
        option.classList.toggle('is-taken', handle !== current && taken.indexOf(handle) !== -1);
        option.hidden = false;
      });

      var field = picker.querySelector('[data-kop6-picker-search]');
      if (field) field.value = '';
      var pc = picker.querySelector('[data-kop6-picker-clear]');
      if (pc) pc.hidden = true;
      var empty = picker.querySelector('[data-kop6-picker-empty]');
      if (empty) empty.hidden = true;

      picker.classList.add('is-open');
      lockScroll();
      setTimeout(function () { if (field) field.focus(); }, 60);
    }

    function closePicker(picker) {
      if (!picker) return;
      picker.classList.remove('is-open');
      unlockScroll();
      if (activeSlot && typeof activeSlot.focus === 'function') activeSlot.focus();
      activeSlot = null;
    }

    function filterPicker(picker, query) {
      var value = String(query || '').trim().toLowerCase();
      var visible = 0;
      candidates(picker).forEach(function (option) {
        var haystack = (option.getAttribute('data-search') || option.getAttribute('data-name') || '').toLowerCase();
        var match = !value || haystack.indexOf(value) !== -1;
        option.hidden = !match;
        if (match) visible++;
      });
      var empty = picker.querySelector('[data-kop6-picker-empty]');
      if (empty) empty.hidden = visible !== 0;
    }

    /* ------------------------------------------------------------------
       Adding to cart

       The first build let Concept's own ProductForm collect the bundle inputs
       and post one multi item /cart/add.js. That is correct on a stock theme,
       but this store's drawer is UpCart, which intercepts the add for its own
       flow, and only the main variant reached the cart.

       So the buy box now owns the add whenever a multi kit tier is selected.
       The listener is on WINDOW in the capture phase, which is the earliest
       point a submit event can be seen, ahead of any handler bound on the
       document or on the form element itself.

       The request goes through window.fetch on purpose: UpCart and the theme
       both watch that call, so the drawer opens and refreshes on its own
       exactly as it does for a normal add.
       ------------------------------------------------------------------ */

    function boxForForm(form) {
      var id = form.getAttribute('id');
      if (!id) return null;
      return doc.querySelector('[data-kop6-buybox][data-kop6-scope="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
    }

    function cartItems(box, form, tier) {
      /* NEVER read form.id here. A form with an <input name="id"> shadows the
         element's own id property and returns that input element. */
      var idInput = form.querySelector('[name="id"]');
      var qtyInput = form.querySelector('[name="quantity"]');
      var quantity = parseInt(qtyInput && qtyInput.value, 10) || 1;

      var properties = {};
      form.querySelectorAll('[name^="properties["]').forEach(function (field) {
        if (field.type === 'checkbox' && !field.checked) return;
        var key = /properties\[(.*?)\]/.exec(field.name);
        if (!key || !field.value) return;
        /* The Kaching app writes its own bundle payload into the product form
           (properties[__kaching_bundles], carrying a deal id and "main": true).
           On the W68 arm that widget is not the one the shopper used, so
           forwarding it would attach another app's bundle to our line and let
           its discount logic act on a cart it did not build. Theme-added
           properties are still copied; only the bundle apps' own keys are not. */
        if (/^__?kaching/i.test(key[1])) return;
        properties[key[1]] = field.value;
      });

      var main = { id: idInput && idInput.value, quantity: quantity };
      if (Object.keys(properties).length) main.properties = properties;

      var items = [main];
      slotsOf(box, tier).forEach(function (slot) {
        var variant = slot.getAttribute('data-variant');
        if (variant) items.push({ id: variant, quantity: quantity });
      });
      return items;
    }

    function openCart() {
      /* Whichever drawer is installed picks this up. UpCart and the theme both
         listen, and neither minds hearing it twice. */
      doc.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      doc.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      try {
        if (typeof window.upcartToggleCart === 'function') window.upcartToggleCart();
        else if (window.UpCart && typeof window.UpCart.open === 'function') window.UpCart.open();
      } catch (e) { /* the drawer is the app's business, not ours */ }
    }

    /* Which form, if any, this event should be taken over for. */
    function bundleTarget(form) {
      if (!form || form.tagName !== 'FORM') return null;
      if (form.getAttribute('data-type') !== 'add-to-cart-form') return null;
      if (!on('w68')) return null;

      var box = boxForForm(form);
      if (!box) return null;

      var tier = selectedTier(box);
      if (!tier) return null;

      /* One kit is a plain add. Leave it to the theme and to the cart app. */
      if (!slotsOf(box, tier).length) return null;

      return { box: box, tier: tier };
    }

    var adding = false;

    function onSubmitCapture(event) {
      var form = event.target;
      var target = bundleTarget(form);
      if (!target) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      addBundle(form, target.box, target.tier);
    }

    /* UpCart binds its own handler to the add to cart BUTTON and runs its own
       add, so on this store the form's submit event never fires at all. Taking
       the click at window capture is the only point that is guaranteed to come
       first, whichever app is installed. */
    function onClickCapture(event) {
      var button = event.target.closest && event.target.closest('button[type="submit"], input[type="submit"]');
      if (!button) return;

      /* The button lives OUTSIDE the form and points at it with form="...",
         which is what .form resolves. */
      var form = button.form || button.closest('form');
      var target = bundleTarget(form);
      if (!target) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      addBundle(form, target.box, target.tier, button);
    }

    function addBundle(form, box, tier, button) {
      if (adding) return;
      adding = true;

      button = button || form.querySelector('[type="submit"]') || doc.querySelector('[type="submit"][form="' + form.getAttribute('id') + '"]');
      if (button) {
        if (button.getAttribute('aria-disabled') === 'true') { adding = false; return; }
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      }

      var routes = (window.theme && window.theme.routes) || {};
      window.fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: cartItems(box, form, tier) })
      })
        .then(function (response) { return response.json(); })
        .then(function (state) {
          if (state && state.status) throw new Error(state.description || state.message || 'cart error');
          return window.fetch(routes.cart_url || '/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'kopstorm-w6', cart: cart });
              }
              doc.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
              openCart();
            });
        })
        .catch(function (error) {
          /* Never strand the shopper on a dead button: hand the submit back to
             the theme so they still get the kit on the page. */
          console.error('Kopstorm buy box add failed', error);
          var message = form.querySelector('.product-form__error-message');
          if (message) {
            message.hidden = false;
            message.innerText = String(error.message || error);
          }
        })
        .finally(function () {
          adding = false;
          if (button) {
            button.removeAttribute('aria-busy');
            button.removeAttribute('aria-disabled');
          }
        });
    }

    /* ------------------------------------------------------------------
       The total on the button

       The shopper reads the button, not the summary line above it, so the
       tier total goes on it with the list price struck through beside it. The
       theme renders its own price node inside the button when the block is
       configured that way; ours is appended after the label and hidden the
       moment the buy box is not driving the price.
       ------------------------------------------------------------------ */

    function buttonsFor(form) {
      var id = form ? form.getAttribute('id') : null;
      if (!id) return [];
      return Array.prototype.slice.call(
        doc.querySelectorAll('[type="submit"][form="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]')
      );
    }

    /* An app that appends a price to the add to cart label puts a second price
       on the button beside the one the buy box prints. The label is restored
       only when the new text STARTS WITH the pristine label, so a genuine label
       change by the theme (sold out, pre-order, waitlist) is never undone. */
    function restoreLabel(button) {
      var el = button.querySelector('[data-kop6-atclabel]');
      if (el) {
        var pristine = el.getAttribute('data-kop6-atclabel');
        if (pristine) {
          var text = el.textContent;
          if (text !== pristine && text.indexOf(pristine) === 0) el.textContent = pristine;
        }
      }
      /* r12: the app also writes its deal price into the STICKY buy button,
         where the theme's label span is hidden on mobile and the appended
         price lands beside the icon as a bare text node inside .btn-text —
         which is what put "$237." inside the 44px icon-only circle. The theme
         never renders bare text directly in .btn-text (only the icon and the
         label span), so a non-whitespace text node there is app-written by
         definition and safe to remove. Element children are left alone. */
      var host = button.querySelector('.btn-text');
      if (!host) return;
      var node = host.firstChild;
      while (node) {
        var next = node.nextSibling;
        if (node.nodeType === 3 && /\S/.test(node.nodeValue)) host.removeChild(node);
        node = next;
      }
    }

    function watchLabel(button) {
      /* r12: observe the whole .btn-text, not only the label span — the app
         writes outside the span on the sticky button. Removing a text node or
         restoring the span fires the observer again, but the second pass finds
         nothing to change, so this cannot loop. */
      var el = button.querySelector('.btn-text') || button.querySelector('[data-kop6-atclabel]');
      if (!el || el.hasAttribute('data-kop6-watched')) return;
      el.setAttribute('data-kop6-watched', '');
      /* The app can rewrite the label long after we paint, so watch rather than
         restore once. Writing textContent fires the observer again, but the
         guard above makes the second pass a no-op, so this cannot loop.
         r11: the restore now runs in EVERY arm, not only under w68. The Kaching
         app appends the deal price to the label span in the control arm too,
         which put "$129.99$139.99" inside Add to cart / Join Waitlist on the
         Midnight In Venezia launch. The startsWith guard in restoreLabel means
         a genuine theme label (Sold Out, Pre-order, Join Waitlist) is never
         undone; only appended text is stripped. */
      new MutationObserver(function () {
        restoreLabel(button);
      }).observe(el, { childList: true, characterData: true, subtree: true });
    }

    /* r13: the theme's sticky buy bar (product-sticky-form) keeps printing
       the single kit's title and price while the buy box is on a two or three
       kit tier, so the bar and the button disagree. The bar now carries a set
       line under the title and its own price element next to the theme's
       StickyPrice. Both are created only under w68, and CSS reveals them only
       while data-kop6-set is present, so the control arm is untouched. The
       theme's own StickyPrice is swapped by innerHTML on a variant change and
       our element sits BESIDE it, not inside, so that swap cannot wipe it; the
       variant:change listener repaints the numbers afterwards. */
    function stickyFor(form) {
      var id = form ? form.getAttribute('id') : null;
      if (!id) return null;
      return doc.querySelector('product-sticky-form[form="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
    }

    function paintSticky(box, form, tier) {
      var sticky = stickyFor(form);
      if (!sticky) return;

      var n = tier ? tierNumbers(box, tier) : null;
      var total = n && n.count > 1 ? money(n.total) : null;
      if (!on('w68') || !total) {
        /* Attribute removed rather than elements: the flag can come back on
           mid session from an Intelligems arm, and CSS hides them by attribute. */
        sticky.removeAttribute('data-kop6-set');
        return;
      }

      var title = sticky.querySelector('.product-sticky-form__variant p');
      var priceHost = sticky.querySelector('[id^="StickyPrice"]');
      if (!title || !priceHost) return;

      var line = sticky.querySelector('[data-kop6-sticky-set]');
      if (!line) {
        line = doc.createElement('div');
        line.className = 'kop6-sticky__set text-xs leading-tight';
        line.setAttribute('data-kop6-sticky-set', '');
        title.parentNode.insertBefore(line, title.nextSibling);
      }
      var nameEl = tier.querySelector('.kop6-bb__tiername');
      var name = nameEl ? nameEl.textContent.replace(/\s+/g, ' ').trim() : '';
      var savingText = n.saving > 0 ? money(n.saving) : '';
      var text = name;
      if (savingText) text += (text ? ' \u00b7 ' : '') + fill(t('you_save', 'You save [amount]'), { amount: savingText });
      if (line.textContent !== text) line.textContent = text;

      var price = sticky.querySelector('[data-kop6-sticky-price]');
      if (!price) {
        price = doc.createElement('div');
        price.setAttribute('data-kop6-sticky-price', '');
        priceHost.parentNode.insertBefore(price, priceHost.nextSibling);
      }
      var listText = n.saving > 0 ? money(n.list) : '';
      price.className = 'kop6-sticky__price price flex flex-wrap items-center gap-2 lg:gap-1d5' + (listText ? ' price--on-sale' : '');
      var now = price.querySelector('.kop6-sticky__now');
      if (!now) {
        now = doc.createElement('span');
        now.className = 'price__regular whitespace-nowrap kop6-sticky__now';
        price.appendChild(now);
      }
      if (now.textContent !== total) now.textContent = total;
      var was = price.querySelector('.kop6-sticky__was');
      if (listText) {
        if (!was) {
          was = doc.createElement('span');
          was.className = 'price__sale inline-flex items-center h-auto relative kop6-sticky__was';
          price.appendChild(was);
        }
        if (was.textContent !== listText) was.textContent = listText;
      } else if (was) {
        price.removeChild(was);
      }

      sticky.setAttribute('data-kop6-set', String(n.count));
    }

    function paintButton(box) {
      var form = doc.getElementById(box.getAttribute('data-form-id'));
      if (!form) return;

      var tier = selectedTier(box);
      paintSticky(box, form, tier); /* r13 */
      if (!tier) return;
      var n = tierNumbers(box, tier);
      var total = money(n.total);
      var list = n.saving > 0 ? money(n.list) : '';

      buttonsFor(form).forEach(function (button) {
        var host = button.querySelector('.btn-text') || button;

        watchLabel(button);
        restoreLabel(button); /* r11: all arms, see watchLabel */

        /* The theme's own in-button price would sit beside ours and read as two
           different totals, so it stands down while the buy box is driving. */
        var native = button.querySelector('[id^="BuyButtonPrice"]');
        if (native) native.hidden = on('w68');

        var slot = host.querySelector('[data-kop6-btnprice]') || button.querySelector('[data-kop6-btnprice]');
        if (!on('w68') || !total) {
          /* Hidden rather than removed: the flag can come back on mid-session
             from an Intelligems arm, and rebuilding would flash. */
          if (slot) slot.hidden = true;
          return;
        }
        if (slot) slot.hidden = false;

        if (!slot) {
          slot = doc.createElement('span');
          slot.className = 'kop6-bb__btnprice';
          slot.setAttribute('data-kop6-btnprice', '');
          slot.setAttribute('aria-hidden', 'true');
          host.appendChild(slot);
        }

        slot.textContent = '';
        var now = doc.createElement('span');
        now.className = 'kop6-bb__btnnow';
        now.textContent = total;
        slot.appendChild(now);

        if (list) {
          var was = doc.createElement('s');
          was.className = 'kop6-bb__btnwas';
          was.textContent = list;
          slot.appendChild(was);
        }
      });
    }

    /* --- setup ---------------------------------------------------------- */

    function boxes() {
      return Array.prototype.slice.call(doc.querySelectorAll('[data-kop6-buybox]'));
    }

    function mount() {
      boxes().forEach(function (box) {
        var picker = pickerFor(box);
        /* position: fixed is measured against the nearest transformed
           ancestor, and the product column sits inside elements the theme
           animates. Moving the sheet to body level removes that whole class
           of clipping bug. */
        if (picker && picker.parentNode !== doc.body) doc.body.appendChild(picker);

        if (box.hasAttribute('data-kop6-ready')) return;
        box.setAttribute('data-kop6-ready', '');
        syncInputs(box);
        syncSummary(box);
        if (window.theme && window.theme.Currency) repaintAll(box);
        paintButton(box);
      });
    }

    function init() {
      mount();

      /* Window capture: the earliest either event can be intercepted. Click is
         the one that matters on this store, submit is the belt to the braces. */
      window.addEventListener('click', onClickCapture, true);
      window.addEventListener('submit', onSubmitCapture, true);

      doc.addEventListener('click', function (event) {
        var node = event.target;

        var tierBtn = node.closest && node.closest('[data-kop6-tierbtn]');
        if (tierBtn) {
          event.preventDefault();
          var tierBox = tierBtn.closest('[data-kop6-buybox]');
          selectTier(tierBox, tierBtn.closest('[data-kop6-tier]'));
          return;
        }

        var slot = node.closest && node.closest('[data-kop6-slot]');
        if (slot) {
          event.preventDefault();
          openPicker(slot);
          return;
        }

        var pclear = node.closest && node.closest('[data-kop6-picker-clear]');
        if (pclear) {
          event.preventDefault();
          var sheet2 = pclear.closest('[data-kop6-picker]');
          var field2 = sheet2.querySelector('[data-kop6-picker-search]');
          field2.value = '';
          pclear.hidden = true;
          filterPicker(sheet2, '');
          field2.focus();
          return;
        }

        var close = node.closest && node.closest('[data-kop6-picker-close]');
        if (close) {
          event.preventDefault();
          closePicker(close.closest('[data-kop6-picker]'));
          return;
        }

        var option = node.closest && node.closest('[data-kop6-option]');
        if (option && activeSlot) {
          event.preventDefault();
          if (option.classList.contains('is-taken')) return;
          var optBox = activeSlot.closest('[data-kop6-buybox]');
          var tier = tierOfSlot(optBox, activeSlot);
          if (!tier) return;
          paintSlot(activeSlot, option);
          resolveCollisions(optBox, tier, activeSlot);
          repaintTier(optBox, tier);
          syncInputs(optBox);
          syncSummary(optBox);
          paintButton(optBox);
          closePicker(option.closest('[data-kop6-picker]'));
        }
      });

      doc.addEventListener('input', function (event) {
        var field = event.target.closest && event.target.closest('[data-kop6-picker-search]');
        if (!field) return;
        var sheet = field.closest('[data-kop6-picker]');
        var clear = sheet.querySelector('[data-kop6-picker-clear]');
        if (clear) clear.hidden = field.value.length === 0;
        filterPicker(sheet, field.value);
      });

      doc.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          var open = doc.querySelector('[data-kop6-picker].is-open');
          if (open) closePicker(open);
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        var tierBtn = event.target.closest && event.target.closest('[data-kop6-tierbtn]');
        if (!tierBtn) return;
        event.preventDefault();
        var navBox = tierBtn.closest('[data-kop6-buybox]');
        var list = tiers(navBox);
        var index = list.indexOf(tierBtn.closest('[data-kop6-tier]'));
        var next = list[event.key === 'ArrowDown' ? index + 1 : index - 1];
        if (!next) return;
        selectTier(navBox, next);
        var button = next.querySelector('[data-kop6-tierbtn]');
        if (button) button.focus();
      });

      doc.addEventListener('kopstorm:flag', function () {
        boxes().forEach(function (box) {
          syncInputs(box);
          paintButton(box);
        });
      });

      /* Single variant kits are the norm here, but a variant switch still has
         to move the base price or every tier total goes stale. */
      doc.addEventListener('variant:change', function (event) {
        var variant = event.detail && event.detail.variant;
        if (!variant || typeof variant.price !== 'number') return;
        var compare = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price
          ? variant.compare_at_price
          : variant.price;
        boxes().forEach(function (box) {
          box.setAttribute('data-base-price', String(variant.price));
          box.setAttribute('data-base-compare', String(compare));
          repaintAll(box);
        });
      });

      /* theme.js is deferred, so the money formatter may not exist yet. The
         server rendered prices are already correct, so the first repaint only
         has to happen once the formatter is available. */
      if (!(window.theme && window.theme.Currency)) {
        window.addEventListener('load', function () { boxes().forEach(repaintAll); }, { once: true });
      }

      /* The quick view modal fetches a product section after load, so a buy
         box can appear at any time. Section rerenders do the same. */
      doc.addEventListener('shopify:section:load', mount);
      if (window.MutationObserver) {
        new MutationObserver(function (records) {
          for (var i = 0; i < records.length; i++) {
            if (records[i].addedNodes.length) { mount(); return; }
          }
        }).observe(doc.body, { childList: true, subtree: true });
      }
    }

    return { init: init };
  })();

  function boot() {
    Search.init();
    BuyBox.init();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();